import { type LucideIcon, TrendingUp, TrendingDown } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'

interface MetricCardProps {
  title: string
  value: string | number
  trend?: number
  icon: LucideIcon
  description?: string
}

export function MetricCard({ title, value, trend, icon: Icon, description }: MetricCardProps) {
  const isPositive = trend && trend > 0
  const isP1Alert = (title.includes('Alerts') || title.includes('P1')) && Number(value) > 0
  const isSlaRisk = title.includes('SLA') && parseFloat(String(value)) < 98

  return (
    <Card className={cn(
      "p-6 bg-slate-900/50 border-slate-800 backdrop-blur-sm group transition-all duration-500 relative overflow-hidden",
      isP1Alert ? "border-rose-500/50 shadow-lg shadow-rose-500/10" :
      isSlaRisk ? "border-amber-500/50 shadow-lg shadow-amber-500/10" :
      "hover:border-slate-700"
    )}>
      <div className="flex justify-between items-start mb-4 relative z-10">
        <div className={cn(
          "p-2.5 rounded-lg bg-slate-800/50 transition-colors",
          isP1Alert ? "text-rose-400 bg-rose-500/10" :
          isSlaRisk ? "text-amber-400 bg-amber-500/10" :
          "text-slate-400 group-hover:text-blue-400"
        )}>
          <Icon className="size-5" />
        </div>
        {trend !== undefined && (
          <div className={cn(
            "flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full",
            isPositive ? "bg-emerald-500/10 text-emerald-400" : "bg-rose-500/10 text-rose-400"
          )}>
            {isPositive ? <TrendingUp className="size-3" /> : <TrendingDown className="size-3" />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <div className="relative z-10">
        <p className={cn(
          "text-sm font-medium uppercase tracking-wider",
          isP1Alert ? "text-rose-400" : isSlaRisk ? "text-amber-400" : "text-slate-500"
        )}>{title}</p>
        <h3 className="text-3xl font-bold text-white mt-1 font-mono">{value}</h3>
        {description && <p className="text-xs text-slate-400 mt-2">{description}</p>}
      </div>
    </Card>
  )
}
